import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CustomerContext = createContext(null);

export const useCustomer = () => useContext(CustomerContext);

export const CustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeader = () => {
    const token = localStorage.getItem('customerToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem('customerToken');
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/customer/profile`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomer(res.data);
      setWishlist(res.data.wishlist || []);
    } catch {
      localStorage.removeItem('customerToken');
      setCustomer(null);
      setWishlist([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const login = async (phone, name) => {
    const res = await axios.post(`${BACKEND_URL}/api/customer/login`, { phone, name });
    localStorage.setItem('customerToken', res.data.token);
    setCustomer(res.data.customer);
    setWishlist(res.data.customer.wishlist || []);
    return res.data.customer;
  };

  const logout = () => {
    localStorage.removeItem('customerToken');
    setCustomer(null);
    setWishlist([]);
  };

  const addToWishlist = async (productId) => {
    if (!customer) return false;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/customer/wishlist/${productId}`, {}, { headers: getAuthHeader() });
      setWishlist(res.data.wishlist);
      return true;
    } catch { return false; }
  };

  const removeFromWishlist = async (productId) => {
    if (!customer) return false;
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/customer/wishlist/${productId}`, { headers: getAuthHeader() });
      setWishlist(res.data.wishlist);
      return true;
    } catch { return false; }
  };

  const isInWishlist = (productId) => wishlist.includes(productId);

  return (
    <CustomerContext.Provider value={{ customer, wishlist, loading, login, logout, addToWishlist, removeFromWishlist, isInWishlist }}>
      {children}
    </CustomerContext.Provider>
  );
};
